import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Paper, Stack } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import PageHeader from "../../../../components/common/PageHeader";
import ActionIconButton from "../../../../components/common/ActionIconButton";
import PageLoader from "../../../../components/common/PageLoader";
import ChipList from "../../../../components/common/ChipList";
import TruncatedText from "../../../../components/common/TruncatedText";
import EmptyState from "../../../../components/common/EmptyState";
import SearchFilterBar from "../../../../components/common/SearchFilterBar";
import DataTable from "../../../../components/DataTable";
import { appActions } from "../../../../redux/actions";
import { getApiErrorMessage } from "../../../../helpers";
import { tenantPermissionService } from "../../services";
import useAuth from "../../../../hooks/useAuth";
import {
  collectPermissionSuggestions,
  matchesPermissionSearch,
  mergePermissionDetail,
  normalizePermission,
  normalizePermissionList,
  sortPermissions,
} from "../../utils/normalizePermission";
import PermissionFormDialog from "../PermissionFormDialog";

const PermissionManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTenantId, tenantId } = useAuth();
  const requestTenantId = activeTenantId || tenantId;
  const listBase = location.pathname.startsWith("/tenant-")
    ? "/tenant-permissions"
    : "/permissions";

  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [appliedName, setAppliedName] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const pendingCreatedRef = React.useRef(null);

  const suggestions = useMemo(
    () => collectPermissionSuggestions(permissions),
    [permissions]
  );

  const loadPermissions = useCallback(
    async (name = appliedName, savedPermission = null) => {
      setLoading(true);
      setError("");
      try {
        const payload = await tenantPermissionService.getAllTenantPermissions(
          requestTenantId
        );
        const pending = savedPermission || pendingCreatedRef.current;
        let normalized = sortPermissions(
          normalizePermissionList(payload).filter((item) =>
            matchesPermissionSearch(item, { name })
          )
        );

        // Keep a just-created permission visible even if list API lags.
        if (
          pending?.name &&
          !normalized.some(
            (item) =>
              String(item.name).toLowerCase() ===
              String(pending.name).toLowerCase()
          ) &&
          matchesPermissionSearch(pending, { name })
        ) {
          normalized = sortPermissions([
            normalizePermission(pending),
            ...normalized,
          ]);
        } else if (pending) {
          const foundInApi = normalized.some(
            (item) =>
              String(item.name).toLowerCase() ===
              String(pending.name).toLowerCase()
          );
          normalized = sortPermissions(
            normalized.map((item) =>
              item.name === pending.name ||
              item.permissionId === pending.permissionId
                ? mergePermissionDetail(item, pending)
                : item
            )
          );
          if (foundInApi) {
            pendingCreatedRef.current = null;
          }
        }

        setPermissions(normalized);
      } catch (err) {
        const message = getApiErrorMessage(err);
        setPermissions([]);
        setError(message);
        dispatch(appActions.apiErrorMessage(message));
      } finally {
        setLoading(false);
      }
    },
    [appliedName, dispatch, requestTenantId]
  );

  useEffect(() => {
    loadPermissions(appliedName);
  }, [appliedName, loadPermissions]);

  const openDetails = useCallback(
    (row) => {
      const id = row?.name || row?.permissionId;
      if (!id) return;
      navigate(`${listBase}/view/${encodeURIComponent(id)}`, {
        state: { permission: row },
      });
    },
    [listBase, navigate]
  );

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      const created = await tenantPermissionService.createTenantPermission(
        payload,
        requestTenantId
      );
      const savedPermission = normalizePermission({
        name: payload.name,
        ...(created && typeof created === "object" ? created : {}),
        updatedAt: new Date().toISOString(),
      });
      pendingCreatedRef.current = savedPermission;
      dispatch(
        appActions.apiSuccessMessage(
          created?._alreadyExisted
            ? "Permission already existed — shown in the list."
            : "Permission created successfully."
        )
      );
      setFormOpen(false);
      setNameFilter("");
      setAppliedName("");
      await loadPermissions("", savedPermission);
    } catch (err) {
      throw new Error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Permission",
        flex: 1.2,
        minWidth: 180,
        renderCell: (params) => <TruncatedText value={params.value} />,
      },
      {
        field: "allow",
        headerName: "Allow",
        flex: 1.4,
        minWidth: 220,
        sortable: false,
        valueGetter: (value, row) =>
          Array.isArray(row?.allow) ? row.allow.join(", ") : "",
        renderCell: (params) => (
          <ChipList items={params.row.allow} maxVisible={2} />
        ),
      },
      {
        field: "deny",
        headerName: "Deny",
        flex: 1,
        minWidth: 180,
        sortable: false,
        valueGetter: (value, row) =>
          Array.isArray(row?.deny) ? row.deny.join(", ") : "",
        renderCell: (params) => (
          <ChipList items={params.row.deny} maxVisible={2} />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <ActionIconButton
            title="View Details"
            icon={<VisibilityOutlinedIcon fontSize="small" />}
            onClick={() => openDetails(params.row)}
          />
        ),
      },
    ],
    [openDetails]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        sx={{ mb: 1 }}
      >
        <PageHeader
          title="Permissions"
          subtitle="Manage tenant permissions."
          sx={{ mb: 0 }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => setFormOpen(true)}
          >
            Add Permission
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => loadPermissions(appliedName)}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <SearchFilterBar
        fields={[
          {
            key: "name",
            label: "Permission",
            value: nameFilter,
            onChange: (e) => setNameFilter(e.target.value),
            width: 280,
          },
        ]}
        onSearch={() => setAppliedName(nameFilter.trim())}
        onClear={() => {
          setNameFilter("");
          setAppliedName("");
        }}
      />

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          minHeight: 360,
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading && !permissions.length ? (
          <PageLoader text="Loading permissions..." />
        ) : !loading && !permissions.length ? (
          <EmptyState
            title="No permissions found"
            description="No permissions found for the current filters."
          />
        ) : (
          <DataTable
            rows={permissions}
            columns={columns}
            pageSize={20}
            showPagination
            loading={loading}
            autoHeight
            getRowId={(row) => row.permissionId || row.name}
            paginationMode="client"
          />
        )}
      </Paper>

      <PermissionFormDialog
        open={formOpen}
        mode="create"
        permission={null}
        suggestions={suggestions}
        loading={saving}
        onClose={() => setFormOpen(false)}
        onSubmit={handleCreate}
      />
    </Box>
  );
};

export default PermissionManagement;
